﻿import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Link , useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useVerifyEmailMutation } from "@/features/organizers/api";
import { ApiError } from "@/lib/api-client";

type Status = "verifying" | "success" | "error";

export function VerifyEmailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const verify = useVerifyEmailMutation();

  const [status, setStatus] = useState<Status>("verifying");
  const [errorMessage, setErrorMessage] = useState<string>("");

  // StrictMode'da effect iki kez çalışır, token'ı bir kez gönderdiğimizden emin olalım.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const token = searchParams.get("token");
    if (!token) {
      setStatus("error");
      setErrorMessage("Doğrulama linki eksik.");
      return;
    }

    verify.mutate(
      { token },
      {
        onSuccess: () => {
          setStatus("success");
          setTimeout(() => {
            navigate("/", { replace: true });
          }, 1500);
        },
        onError: (err) => {
          setStatus("error");
          setErrorMessage(
            err instanceof ApiError
              ? err.detail
              : "Doğrulama sırasında bir hata oluştu.",
          );
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-muted/40">
      <Card className="w-full max-w-md text-center">
        <CardHeader className="space-y-4">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full">
            {status === "verifying" && <Loader2 className="h-10 w-10 animate-spin text-primary" />}
            {status === "success" && <CheckCircle2 className="h-14 w-14 text-green-600" />}
            {status === "error" && <XCircle className="h-14 w-14 text-destructive" />}
          </div>
          <div className="space-y-2">
            <CardTitle className="text-2xl">
              {status === "verifying" && "Email doğrulanıyor..."}
              {status === "success" && "Email Doğrulandı!"}
              {status === "error" && "Doğrulama Başarısız"}
            </CardTitle>
            <CardDescription>
              {status === "verifying" && "Lütfen bekle, hesabını aktifleştiriyoruz."}
              {status === "success" && "Hesabın aktif. Dashboard'a yönlendiriliyorsun..."}
              {status === "error" && errorMessage}
            </CardDescription>
          </div>
        </CardHeader>
        {status === "error" && (
          <CardContent className="space-y-3">
            <Button asChild variant="outline" className="w-full">
              <Link to="/login">Giriş Yap</Link>
            </Button>
            <Button asChild variant="ghost" className="w-full">
              <Link to="/signup">Yeni Hesap Oluştur</Link>
            </Button>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
